// Copyright (c) Docugami, Inc. All rights reserved.

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDgDownloader } from 'hooks/downloader';
import { Telemetry } from 'telemetry/TelemetryManager';
import { DownloadDgmlxCommandSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { IDocumentCommand } from 'components/file_management/DocumentCommand';
import { AppState } from 'store';
import { FileManagerInstance } from 'filemanager/FileManager';
import { RequestState, useAsyncHelper } from './AsyncHelper';
import { isDirectory } from 'components/file_management/file_helpers';
import { IngestionStatus } from 'networking/api';

/**
 * Command used for downloading dgxmlx zip file for current file
 */
export const useDownloadDgmlxCommand = (items: string[]): IDocumentCommand => {
    const [isActive, setIsActive] = useState(false);
    const { execute: ensureFile, state: ensureFileState } = useAsyncHelper("Ensure File", items);
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization.id);
    const dgDownloader = useDgDownloader(orgId);

    function onClick() {
        if (isActive) {
            return;
        }

        setIsActive(true);

        Telemetry.trackEvent<DownloadDgmlxCommandSchema>({
            eventName: TelemetryEvent.DownloadDgmlxCommand
        });
        dgDownloader.downloadDgmlx(items[0]);
    }

    useEffect(() => {
        if (items.length === 1 && !isDirectory(items[0]) && ensureFileState === RequestState.None) {
            ensureFile(FileManagerInstance.fetchFiles(items));
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [ensureFile, ensureFileState]);

    useEffect(() => {
        if (!isActive) {
            return;
        }

        if (dgDownloader.canDownloadFiles()) {
            setIsActive(false);
        }

    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dgDownloader]);

    function isEnabled() {
        let isEnabled = false;
        if (ensureFileState === RequestState.Complete) {
            const file = FileManagerInstance.getFile(items[0]);
            const ingestionStatus = file?.properties.ingestionStatus;
            isEnabled = ingestionStatus === IngestionStatus.Succeeded || ingestionStatus === IngestionStatus.Failed;
        }

        return isEnabled;
    }

    return {
        id: "DownloadDgmlx",
        isActive: isActive,
        label: isActive ? "Downloading Dgmlx..." : "Download Dgmlx",
        isEnabled: isEnabled(),
        visual: null,
        execute: onClick
    }
}
